import { inject, Injectable } from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { UserFacadeService } from '@services/store-facade'
import { RecordCellsService } from '@services/record-cells.service'
import { UpdateService } from '@services/update.service'
import { BaseMainBusinessRecord, FieldTypes, RecordGroup } from '@core/models'
import { filter } from 'lodash-es'
import { EMPTY, switchMap } from 'rxjs'
import { UserEntities } from '@models/ui'

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class ViewRecordsValueUpdateService {
    private readonly userFacadeService = inject(UserFacadeService)
    private readonly updateService = inject(UpdateService)
    private readonly recordCellsService = inject(RecordCellsService)

    recordsRevision: Map<string, number> = new Map()

    callDeletedUsers(records: BaseMainBusinessRecord[] | Map<string, RecordGroup>): void {
        const recordArray = records instanceof Array ? records : this.joinGroups(records)

        this.userFacadeService.userEntities$
            .pipe(
                untilDestroyed(this),
                switchMap((users) => {
                    const deletedUsers = this.getDeletedUsersArray(recordArray, users)
                    if (!deletedUsers.length) return EMPTY

                    return this.updateService.userInfo(deletedUsers, true)
                }),
            )
            .subscribe()
    }

    private joinGroups(group: Map<string, RecordGroup>) {
        group.values()
        let businessRecords: BaseMainBusinessRecord[] = []
        for (const value of group.values()) {
            for (const record of value.data) {
                businessRecords.push(record)
            }
        }
        return businessRecords
    }

    private getDeletedUsersArray(records: BaseMainBusinessRecord[], users: UserEntities) {
        const allUsersArray = records.reduce((acc, record) => {
            if (this.recordsRevision.get(record.guid) === record.revision) {
                return acc
            }
            this.recordsRevision.set(record.guid, record.revision)
            this.getPeopleCells(record).forEach((cell) => {
                cell.value.split(',').forEach((guid) => {
                    if (guid && !acc.has(guid) && !users[guid]) {
                        acc.add(guid)
                    }
                })
            })

            return acc
        }, new Set<string>())

        return Array.from(allUsersArray)
    }

    private getPeopleCells(record: BaseMainBusinessRecord) {
        return filter(
            this.recordCellsService.getRecordCells<BaseMainBusinessRecord>(record),
            (cell) => {
                return [FieldTypes.ASSIGNEE, FieldTypes.WATCH, FieldTypes.PEOPLE].includes(
                    cell.field_type as FieldTypes,
                )
            },
        )
    }
}
